import { useSubmit } from 'hooks';
import { format } from 'date-fns';
import { useQueryClient } from 'react-query';
import { VENDOR_DETAIL_SEARCH } from 'constants/queryKey';
import { useDispatch, useSelector } from 'react-redux';
import { searchContractSubscriptionDetails } from 'actions/client';
import { setErrors } from 'actions/errorHandler';
import { VendorCustomerDetail } from 'pages/VendorDetail/type';
import get from 'lodash/get';

export const useCyberScoreDomainRatingActivation = (payload: VendorCustomerDetail, shouldShowDomainRating?: boolean) => {
  const clientContract = useSelector((state: any) => state.client.latestContractConfiguration);
  const cyberScoreActivationLimitRemaining = get(clientContract, 'CyberScoreActivationLimitRemaining', 0);
  const isReachedDomainRatingLimit = cyberScoreActivationLimitRemaining <= 0;

  const mutation = useSubmit(`client/customer-vendor/${payload.CustomerVendorId}/engagement`);

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const refetch = () => {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes(VENDOR_DETAIL_SEARCH),
    });
    // Because the searchContractSubscriptionDetails trigger reload whole app, it makes all component completely re-render,
    // we need to store the showDomainRating state in sessionStorage to persist state
    sessionStorage.setItem('showDomainRating', JSON.stringify(shouldShowDomainRating));
    dispatch(searchContractSubscriptionDetails());
  };

  const activate = async () => {
    try {
      const body = buildRequestPayload(payload, true);
      await mutation.mutateAsync({ ...body, __axios: { method: 'PUT' } });
    } catch (error: any) {
      console.error(error);
      if (error?.response?.data?.Errors) dispatch(setErrors(error.response.data.Errors));
    } finally {
      refetch();
    }
  };

  const deactivate = async () => {
    try {
      const body = buildRequestPayload(payload, false);
      await mutation.mutateAsync({ ...body, __axios: { method: 'PUT' } });
    } catch (error: any) {
      console.error(error);
      if (error?.response?.data?.Errors) dispatch(setErrors(error.response.data.Errors));
    } finally {
      refetch();
    }
  };

  return {
    isReachedDomainRatingLimit,
    activate,
    deactivate,
  };
};

const buildRequestPayload = (payload: VendorCustomerDetail, cyberScoreActivationStatus: boolean) => {
  return {
    VendorNameForCustomer: payload.VendorAlias,
    VendorIdForCustomer: payload.VendorIdForCustomer,
    Status: payload.Status,
    EndOfRelationShipDate: payload.EndOfRelationShipDate ? format(new Date(payload.EndOfRelationShipDate), 'MM/dd/yyyy') : null,
    IsTPIR: typeof payload.IsTPIR === 'number' ? payload.IsTPIR : Number(payload.IsTPIR),
    CyberScoreActivationStatus: cyberScoreActivationStatus ? 1 : 0,
  };
};
